// SaveQuickRunDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Save } from 'lucide-react';

/**
 * Dialog for saving a prompt configuration as a Quick Run
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {string} props.quickRunName - Name for the quick run
 * @param {function} props.onNameChange - Callback when name changes
 * @param {string} props.contextPreview - The custom input/context to save
 * @param {string} props.taskType - The selected task type
 * @param {function} props.onSave - Callback when save is clicked
 * @param {function} props.onCancel - Callback when cancel is clicked
 */
export const SaveQuickRunDialog = ({
  open,
  onOpenChange,
  quickRunName,
  onNameChange,
  contextPreview,
  taskType,
  onSave,
  onCancel
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Save className="h-5 w-5 text-amber-500" />
            Save as Quick Run
          </DialogTitle>
          <DialogDescription>
            Save this prompt configuration for one-click re-runs in the future.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="quick-run-name">Name</Label>
            <Input
              id="quick-run-name"
              placeholder="e.g., Weekly Marketing Analysis"
              value={quickRunName}
              onChange={(e) => onNameChange(e.target.value)}
              className="mt-1"
              data-testid="quick-run-name-input"
            />
          </div>
          
          <div className="bg-slate-50 p-3 rounded-lg border">
            <Label className="text-xs text-muted-foreground">Context Preview</Label>
            <p className="text-sm mt-1 line-clamp-3">{contextPreview}</p>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline">{taskType?.replace('_', ' ') || 'Custom Task'}</Badge>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="quick-run-cancel">
            Cancel
          </Button>
          <Button
            onClick={onSave}
            className="bg-amber-500 hover:bg-amber-600"
            data-testid="quick-run-save"
          >
            <Save className="h-4 w-4 mr-2" />
            Save Quick Run
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveQuickRunDialog;
